import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { RevealLine } from "./motion";
import { IMG } from "./data";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative h-[100svh] min-h-[640px] w-full overflow-hidden">
      {/* Parallax background */}
      <motion.div style={{ y: imgY, scale }} className="absolute inset-0 z-0">
        <img src={IMG.hero} alt="Industriereinigung" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0d0d0f]/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0f] via-[#0d0d0f]/30 to-[#0d0d0f]/60" />
      </motion.div>

      {/* Rotating mandala watermark */}
      <div className="absolute right-[-8%] top-[12%] z-[1] hidden lg:block">
        <div className="spin-slow h-[520px] w-[520px] rounded-full border border-[#4fd1c5]/10" />
      </div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-end px-6 pb-24 lg:px-10 lg:pb-32">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mb-8 flex items-center gap-4"
        >
          <span className="h-px w-12 bg-[#4fd1c5]" />
          <span className="font-mono-spc text-[11px] tracking-[0.35em] text-[#4fd1c5] uppercase">
            Gebäude- & Industriereinigung · Basel
          </span>
        </motion.div>

        <h1 className="font-head font-light uppercase tracking-tighter text-[13vw] leading-[0.9] sm:text-[10vw] lg:text-[7.5rem] lg:leading-[0.88]">
          <RevealLine delay={0.45} className="text-metal">Präzision,</RevealLine>
          <RevealLine delay={0.58}>
            <span className="text-outline">die man</span>
          </RevealLine>
          <RevealLine delay={0.71} className="text-metal">sieht.</RevealLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9 }}
          className="mt-10 max-w-xl text-base font-light leading-relaxed text-[#c0c5ce]/80 sm:text-lg"
        >
          Von der klassischen Unterhaltsreinigung bis zur anspruchsvollen
          Industriereinigung — kompromisslos sauber, termingerecht und mit
          Schweizer Sorgfalt ausgeführt.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.25, duration: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-services"
            onClick={() => document.getElementById("leistungen")?.scrollIntoView({ behavior: "smooth" })}
            className="group flex items-center gap-3 rounded-sm bg-[#4fd1c5] px-8 py-4 font-mono-spc text-[11px] uppercase tracking-[0.22em] text-[#0d0d0f] transition-colors duration-300 hover:bg-white"
          >
            Leistungen entdecken
            <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" strokeWidth={1.5} />
          </button>
          <button
            data-testid="hero-cta-contact"
            onClick={() => document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" })}
            className="rounded-sm border border-white/20 px-8 py-4 font-mono-spc text-[11px] uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:border-[#4fd1c5] hover:text-[#4fd1c5]"
          >
            Kontakt aufnehmen
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
}
